import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { AuthShell, ErrorNote } from "./Login";
import { ArrowRight } from "lucide-react";

const inputClass =
  "w-full rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] px-4 py-3.5 text-base font-body text-[#1F2937] outline-none transition-colors focus:border-[#0D9488]";

export default function StudentLogin() {
  const { studentLogin } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [classCode, setClassCode] = useState("");
  const [showClassCode, setShowClassCode] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await studentLogin(username.trim().toLowerCase(), password, classCode.trim().toUpperCase() || undefined);
      navigate("/");
    } catch (err) {
      // Backend returns 409 with { code: "class_code_required" } when the same
      // username exists in multiple classrooms — reveal the class code field.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "class_code_required") {
        setShowClassCode(true);
        setError(detail.message || "Ask your teacher for your class code, then log in again.");
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Student Log In"
      subtitle="Enter the username and password your teacher or parent gave you."
      footer={
        <>
          Are you a teacher or parent?{" "}
          <Link to="/login" className="font-bold text-[#0D9488] hover:underline">Log in here</Link>
        </>
      }
    >
      <form onSubmit={submit}>
        <ErrorNote message={error} />
        <label className="block text-sm font-bold text-[#1F2937] mb-2">Username</label>
        <input data-testid="student-username-input" value={username} onChange={(e) => setUsername(e.target.value)} className={inputClass} placeholder="e.g. maya" autoComplete="username" />
        <label className="block text-sm font-bold text-[#1F2937] mb-2 mt-4">Password</label>
        <input data-testid="student-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} placeholder="••••••••" autoComplete="current-password" />

        {showClassCode ? (
          <div className="mt-4">
            <label className="block text-sm font-bold text-[#1F2937] mb-2">
              Class code
              <span className="ml-2 rounded-full bg-[#FF9E1B]/20 px-2 py-0.5 text-[10px] font-bold uppercase text-[#B45309]">Required</span>
            </label>
            <input data-testid="student-class-code-input" value={classCode} onChange={(e) => setClassCode(e.target.value.toUpperCase())}
              className={`${inputClass} tracking-widest uppercase`} placeholder="ZL-XXXX" autoCapitalize="characters" />
            <p className="mt-1 text-xs font-body text-[#6B7280]">Your teacher or parent will have a short code like <b>ZL-4X8T</b>.</p>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowClassCode(true)}
            data-testid="student-show-class-code"
            className="mt-3 text-xs font-bold text-[#0D9488] hover:underline"
          >
            My teacher gave me a class code
          </button>
        )}

        <button type="submit" disabled={busy} data-testid="student-login-submit" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-4 text-lg font-bold text-white shadow-md transition-all hover:bg-[#0B7F74] active:translate-y-0.5 disabled:opacity-50">
          {busy ? "Logging in..." : "Start Learning"} <ArrowRight size={20} />
        </button>
      </form>
    </AuthShell>
  );
}
